import React, { useId, useMemo, useState } from 'react';
import { cn } from '../../utils/cn';
import { AFRICAN_COUNTRIES, PRIORITY_COUNTRIES_DRC } from '../../constants/countries';

/**
 * Numero de telephone en deux parties : indicatif pays (liste) et numero.
 *
 * La valeur reste UNE chaine « +243 999123456 », celle que le serveur stocke
 * et propage deja (telephoneMobile, Customer.phone). Une valeur existante sans
 * indicatif reconnu est conservee telle quelle dans le numero : aucun compte
 * existant ne voit son numero modifie a l'affichage.
 */

const DEFAULT_CODE = '+243';

// Indicatifs uniques, RDC et pays prioritaires d'abord, les plus longs testes
// en premier pour reconnaitre une valeur existante.
const OPTIONS = (() => {
  const prio = PRIORITY_COUNTRIES_DRC
    .map((code) => AFRICAN_COUNTRIES.find((c) => c.code === code))
    .filter(Boolean) as typeof AFRICAN_COUNTRIES;
  const rest = AFRICAN_COUNTRIES
    .filter((c) => !PRIORITY_COUNTRIES_DRC.includes(c.code))
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  return [...prio, ...rest].filter((c) => !!c.phoneCode);
})();
const CODES_BY_LENGTH = Array.from(new Set(OPTIONS.map((c) => c.phoneCode)))
  .sort((a, b) => b.length - a.length);

export function splitPhone(value?: string | null): { code: string; number: string } {
  const raw = (value || '').trim();
  if (!raw) return { code: DEFAULT_CODE, number: '' };
  const compact = raw.replace(/\s+/g, '');
  const code = CODES_BY_LENGTH.find((c) => compact.startsWith(c));
  if (!code) return { code: DEFAULT_CODE, number: raw };
  // Retire l'indicatif en gardant la mise en forme du reste du numero.
  let i = 0;
  let seen = 0;
  while (i < raw.length && seen < code.length) {
    if (!/\s/.test(raw[i])) seen += 1;
    i += 1;
  }
  return { code, number: raw.slice(i).trim() };
}

/**
 * Valeur d'attente prise pour un numéro (« +243 000 000 000 », posée autrefois
 * par le serveur quand rien n'était saisi) : jamais affichée comme un numéro.
 */
export function isPlaceholderPhone(value?: string | null): boolean {
  const digits = (value || '').replace(/\D/g, '');
  return !digits || /^0+$/.test(digits) || /^\d{1,3}0{6,}$/.test(digits);
}

/**
 * Contrôle d'un numéro saisi : null si vide ou valide, sinon le message.
 * RDC : 9 chiffres après +243 (le 0 national est toléré), mobile en 8 ou 9.
 */
export function phoneError(value?: string | null): string | null {
  const raw = (value || '').trim();
  if (!raw) return null;
  const { code, number } = splitPhone(raw);
  if (/[^\d\s.\-()]/.test(number)) return 'Le numéro ne contient que des chiffres.';
  let digits = number.replace(/\D/g, '');
  if (!digits) return null;
  if (/^0+$/.test(digits)) return 'Saisissez le vrai numéro, pas une suite de zéros.';
  if (code === DEFAULT_CODE) {
    digits = digits.replace(/^0/, '');
    if (digits.length < 9) return 'Numéro incomplet : 9 chiffres attendus après +243.';
    if (digits.length > 9) return 'Numéro trop long : 9 chiffres attendus après +243.';
    if (!/^[89]/.test(digits)) return 'Un numéro mobile de RDC commence par 8 ou 9 (après le 0).';
    return null;
  }
  return digits.length < 6 || digits.length > 12 ? 'Numéro invalide pour cet indicatif.' : null;
}

export function joinPhone(code: string, number: string): string {
  const n = number.trim();
  return n ? `${code} ${n}` : '';
}

interface PhoneFieldProps {
  label?: string;
  value?: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  hint?: React.ReactNode;
  /** Message d'erreur affiché sous le champ (remplace l'aide). */
  error?: string | null;
  className?: string;
}

export const PhoneField: React.FC<PhoneFieldProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  hint,
  error,
  className,
}) => {
  const parsed = useMemo(() => splitPhone(value), [value]);
  // Indicatif choisi avant de taper le numero : une valeur vide ne le porte
  // pas, on le garde donc localement.
  const [codeChoisi, setCodeChoisi] = useState(parsed.code);
  // Une valeur d'attente n'est jamais montree comme un numero saisi.
  const number = isPlaceholderPhone(value) ? '' : parsed.number;
  const code = number ? parsed.code : codeChoisi;
  const fieldId = useId();
  const helpId = `${fieldId}-aide`;

  return (
    <div className={cn('min-w-0 space-y-1', className)}>
      {label && <label htmlFor={fieldId} className="block text-sm font-medium text-gray-700">{label}</label>}
      <div
        className={cn(
          // 44 px au doigt sur mobile, 40 px des sm ; jamais plus large que son parent.
          'flex w-full min-w-0 items-stretch min-h-[2.75rem] sm:min-h-[2.5rem] border rounded-lg bg-white overflow-hidden',
          error ? 'border-red-400' : 'border-gray-300',
          disabled ? 'bg-gray-50' : 'focus-within:ring-2 focus-within:ring-[#197ca8] focus-within:border-transparent',
        )}
      >
        <select
          aria-label="Indicatif pays"
          value={code}
          disabled={disabled}
          onChange={(e) => {
            setCodeChoisi(e.target.value);
            onChange(joinPhone(e.target.value, number));
          }}
          className={cn(
            // text-base sur mobile : pas de zoom automatique d'iOS au toucher.
            'w-24 sm:w-28 shrink-0 border-0 border-r border-gray-300 px-2 text-base sm:text-sm bg-transparent focus:outline-none',
            disabled ? 'text-gray-700' : 'text-gray-900 cursor-pointer',
          )}
        >
          {OPTIONS.map((c) => (
            <option key={c.code} value={c.phoneCode}>
              {c.flag} {c.phoneCode}
            </option>
          ))}
        </select>
        <input
          id={fieldId}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={number}
          disabled={disabled}
          placeholder={disabled ? 'Non renseigné' : 'Ex. 812 345 678'}
          aria-invalid={!!error}
          aria-describedby={error || hint ? helpId : undefined}
          onChange={(e) => onChange(joinPhone(code, e.target.value))}
          className="flex-1 min-w-0 border-0 px-3 py-2 text-base sm:text-sm text-gray-900 bg-transparent focus:outline-none"
        />
      </div>
      {error ? (
        <p id={helpId} role="alert" className="text-xs text-red-600">{error}</p>
      ) : hint ? (
        <p id={helpId} className="text-xs text-gray-500">{hint}</p>
      ) : null}
    </div>
  );
};
